import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUI } from '../../context/UIContext';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { useTheme } from '../../context/ThemeContext';
import CartSidebar from './CartSidebar';
import SearchModal from './SearchModal';
import api from '../../services/api';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';

const Navbar = () => {
  const { isCartOpen, openCart, closeCart } = useUI();
  const { isAuthenticated } = useAuth();
  const { cartTotals } = useCart();
  const { theme } = useTheme();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [megaMenus, setMegaMenus] = useState({});
  const [navItems, setNavItems] = useState([]);
  const [openAccordion, setOpenAccordion] = useState(null);
  const [logoUrl, setLogoUrl] = useState('/logo.png');
  const [logoKey, setLogoKey] = useState(Date.now());
  const [logoError, setLogoError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const res = await api.get('/settings/announcement');
        if (res.logoUrl && res.logoUrl !== logoUrl) {
          setLogoUrl(res.logoUrl);
          setLogoKey(Date.now());
        }
      } catch (err) {
        console.error('Failed to fetch logo', err);
      }
    };
    fetchLogo();
  }, []);

  useEffect(() => {
    const fetchNavItems = async () => {
      try {
        const res = await api.get('/nav-items');
        const mapped = res.map(item => ({
          name: item.name,
          href: `/category/${item.name.toLowerCase().replace(/ /g, '-')}`,
          isPink: item.isPink || false,
          isBold: item.isBold || false,
          subItems: item.subItems || []
        }));
        setNavItems(mapped);
      } catch (err) {
        console.error('Failed to fetch nav items:', err);
      }
    };
    fetchNavItems();
  }, []);

  useEffect(() => {
    const fetchAllMegaMenus = async () => {
      try {
        const res = await api.get('/megamenu/all');
        const menusMap = {};
        res.forEach(menu => {
          menusMap[menu.navItem] = menu;
        });
        setMegaMenus(menusMap);
      } catch (err) {
        console.error('Failed to fetch mega menus:', err);
      }
    };
    fetchAllMegaMenus();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 769) setIsMobileMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const hasMegaMenu = hoveredItem && megaMenus[hoveredItem];

  const toggleAccordion = (itemName) => {
    if (openAccordion === itemName) {
      setOpenAccordion(null);
    } else {
      setOpenAccordion(itemName);
    }
  };

  return (
    <>
      <header
        className="sticky top-0 left-0 w-full z-50 bg-white border-b border-gray-200"
        onMouseLeave={() => setHoveredItem(null)}
        style={{ backgroundColor: theme.backgroundColor || '#ffffff' }}
      >
        <div className="max-w-[1440px] mx-auto px-4 py-3 md:py-5 flex items-center justify-between gap-3">
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-1 text-gray-800"
            aria-label="Toggle menu"
          >
            <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" className="w-7 h-7">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5M3.75 17.25h16.5" />
            </svg>
          </button>

          <Link to="/" className="flex flex-col items-center flex-1 justify-center md:flex-none md:items-start md:w-auto">
            <img
              key={logoKey}
              src={logoError ? '/logo.png' : logoUrl}
              alt="ArshiVastra Logo"
              className="h-9 md:h-[36px] w-auto object-contain"
              onError={() => setLogoError(true)}
            />
          </Link>

          <div className="hidden md:flex flex-wrap items-center justify-center gap-x-[28px] gap-y-2 max-w-[900px] mx-auto flex-1">
            {navItems.map((link) => (
              <div
                key={link.name}
                onMouseEnter={() => setHoveredItem(link.name)}
                className="h-full flex items-center"
              >
                <Link
                  to={link.href}
                  className={`text-[12px] uppercase tracking-[1.5px] transition-colors ${
                    link.isPink ? 'text-[#d60167]' : 'text-[#1c1d1d]'
                  } ${link.isBold ? 'font-semibold' : 'font-medium'} hover:text-[#d60167] whitespace-nowrap`}
                  style={{
                    color: link.isPink ? theme.primaryColor : theme.textColor,
                  }}
                >
                  {link.name}
                </Link>
              </div>
            ))}
            {/* Static Shop All link */}
            <Link
              to="/shop-all"
              className="text-[12px] uppercase tracking-[1.5px] font-medium text-[#1c1d1d] hover:text-[#d60167] whitespace-nowrap"
              style={{ color: theme.textColor }}
              onMouseEnter={(e) => e.target.style.color = theme.primaryColor}
              onMouseLeave={(e) => e.target.style.color = theme.textColor}
            >
              Shop All
            </Link>
          </div>

          <div className="flex items-center gap-3 md:gap-5">
            {isAuthenticated ? (
              <Link to="/dashboard" className="flex items-center" style={{ color: theme.textColor }}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
                </svg>
              </Link>
            ) : (
              <Link to="/login" className="flex items-center" style={{ color: theme.textColor }}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
                </svg>
              </Link>
            )}
            <button onClick={() => setIsSearchOpen(true)} className="text-gray-800" aria-label="Search" style={{ color: theme.textColor }}>
              <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" className="w-5 h-5 md:w-5 md:h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.602 10.602Z" />
              </svg>
            </button>
            <button onClick={openCart} className="relative text-gray-800 group" aria-label="Cart" style={{ color: theme.textColor }}>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 transition-colors" style={{ color: theme.primaryColor }}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 1 0-7.5 0v4.5m11.356-1.993 1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 0 1-1.12-1.243l1.264-12A1.125 1.125 0 0 1 5.513 7.5h12.974c.576 0 1.059.435 1.119 1.007ZM8.625 10.5a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm7.5 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
              </svg>
              {cartTotals.itemsCount > 0 && (
                <span className="absolute bottom-[2px] right-[2px] w-2 h-2 rounded-full" style={{ backgroundColor: theme.primaryColor }} />
              )}
            </button>
          </div>
        </div>

        <div className={`md:hidden fixed inset-0 z-[9999] bg-white overflow-y-auto transition-transform duration-300 ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="flex justify-end p-4">
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="text-gray-800 p-1"
              aria-label="Close menu"
            >
              <svg fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" className="w-7 h-7">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="px-6 pb-20">
            {navItems.map((item) => {
              const megaMenuData = megaMenus[item.name];
              const hasChildren = megaMenuData && megaMenuData.columns?.length > 0;
              const isOpen = openAccordion === item.name;

              return (
                <div key={item.name} className="border-b border-gray-200 py-3">
                  <div
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => hasChildren && toggleAccordion(item.name)}
                  >
                    <span
                      className={`text-[16px] uppercase tracking-wider ${
                        item.isPink ? 'text-[#d60167] font-bold' : 'text-[#1c1d1d] font-medium'
                      }`}
                      style={{
                        color: item.isPink ? theme.primaryColor : theme.textColor,
                      }}
                    >
                      {item.name}
                    </span>
                    {hasChildren && (
                      <div className="text-gray-500">
                        {isOpen ? <FiChevronUp size={20} /> : <FiChevronDown size={20} />}
                      </div>
                    )}
                  </div>

                  {hasChildren && isOpen && (
                    <div className="mt-2 pl-4 border-l-2 border-gray-200">
                      {megaMenuData.columns.map((column, colIdx) => (
                        <div key={colIdx} className="py-2">
                          <div className="text-[#d60167] font-bold text-sm mb-2" style={{ color: theme.primaryColor }}>
                            {column.title}
                          </div>
                          <div className="pl-2 space-y-2">
                            {column.items.map((subItem, subIdx) => (
                              <Link
                                key={subIdx}
                                to={subItem.link || `/category/${subItem.name.toLowerCase().replace(/ /g, '-')}`}
                                className="block text-[15px] text-[#1c1d1d] hover:text-[#d60167]"
                                onClick={() => setIsMobileMenuOpen(false)}
                                style={{
                                  color: theme.textColor,
                                }}
                                onMouseEnter={(e) => e.target.style.color = theme.primaryColor}
                                onMouseLeave={(e) => e.target.style.color = theme.textColor}
                              >
                                {subItem.name}
                              </Link>
                            ))}
                            {column.shopAll && (
                              <Link
                                to={column.shopAllLink || `/category/${column.title.toLowerCase().replace(/ /g, '-')}`}
                                className="block text-[15px] font-semibold text-[#1c1d1d] hover:text-[#d60167] mt-1"
                                onClick={() => setIsMobileMenuOpen(false)}
                                style={{
                                  color: theme.textColor,
                                }}
                                onMouseEnter={(e) => e.target.style.color = theme.primaryColor}
                                onMouseLeave={(e) => e.target.style.color = theme.textColor}
                              >
                                Shop All
                              </Link>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {/* Static Shop All link in mobile menu */}
            <div className="border-b border-gray-200 py-3">
              <Link
                to="/shop-all"
                className="block text-[16px] uppercase tracking-wider font-medium text-[#1c1d1d] hover:text-[#d60167]"
                onClick={() => setIsMobileMenuOpen(false)}
                style={{ color: theme.textColor }}
                onMouseEnter={(e) => e.target.style.color = theme.primaryColor}
                onMouseLeave={(e) => e.target.style.color = theme.textColor}
              >
                Shop All
              </Link>
            </div>

            <div className="mt-6 pt-4 border-t border-gray-200">
              {isAuthenticated ? (
                <Link
                  to="/dashboard"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block text-center text-[16px] font-medium text-[#1c1d1d] hover:text-[#d60167]"
                  style={{ color: theme.textColor }}
                >
                  Dashboard
                </Link>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block text-center text-[16px] font-medium text-[#1c1d1d] hover:text-[#d60167]"
                  style={{ color: theme.textColor }}
                >
                  Login / Sign Up
                </Link>
              )}
            </div>
          </div>
        </div>

        {hasMegaMenu && (
          <div
            className="hidden md:block absolute top-full left-0 w-full bg-white shadow-lg border-t border-gray-200 py-10 z-40"
            onMouseEnter={() => setHoveredItem(hoveredItem)}
            onMouseLeave={() => setHoveredItem(null)}
          >
            <div className="max-w-[900px] mx-auto flex justify-center gap-20">
              {megaMenus[hoveredItem].columns?.map((col, idx) => (
                <div key={idx} className="flex flex-col gap-3 min-w-[150px]">
                  <h4 className="text-[13px] font-semibold uppercase tracking-[2px] mb-2" style={{ color: theme.primaryColor }}>
                    {col.title}
                  </h4>
                  {col.items?.map((item, i) => (
                    <Link
                      key={i}
                      to={item.link || `/category/${item.name.toLowerCase().replace(/ /g, '-')}`}
                      className="text-[15px] transition-colors"
                      style={{
                        color: theme.textColor,
                      }}
                      onMouseEnter={(e) => e.target.style.color = theme.primaryColor}
                      onMouseLeave={(e) => e.target.style.color = theme.textColor}
                    >
                      {item.name}
                    </Link>
                  ))}
                  {col.shopAll && (
                    <Link
                      to={col.shopAllLink || `/category/${col.title.toLowerCase().replace(/ /g, '-')}`}
                      className="text-[15px] font-semibold mt-2 transition-colors"
                      style={{
                        color: theme.textColor,
                      }}
                      onMouseEnter={(e) => e.target.style.color = theme.primaryColor}
                      onMouseLeave={(e) => e.target.style.color = theme.textColor}
                    >
                      Shop All
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </header>

      <CartSidebar isOpen={isCartOpen} onClose={closeCart} />
      <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </>
  );
};

export default Navbar;